import { Link } from "react-router-dom";
import Reveal from "@/components/Reveal";
import WordReveal from "@/components/WordReveal";
import { useSeo } from "@/lib/seo";
import { SERVICES_LD, breadcrumb } from "@/lib/structuredData";

const CAPABILITIES = [
  {
    num: "01",
    title: "Product development",
    body: "Drawings, references and rough ideas become makeable products. We translate intent into a tech pack the right factory can read, then build samples against costed targets.",
    deliverables: [
      ["Tech-pack development", "DWG · PDF · BOM"],
      ["Material library", "5 core materials"],
      ["Costed samples", "≤ 3 weeks"],
      ["Golden sample retention", "Per SKU, on site"],
    ],
  },
  {
    num: "02",
    title: "Sourcing",
    body: "We don’t pool factories — we hold a directory across eight clusters, each tied to a material discipline. The shortlist for any brief is three to five workshops we already know.",
    deliverables: [
      ["Vetted factory shortlist", "3–5 per brief"],
      ["Capacity & financial check", "Updated quarterly"],
      ["Floor pricing per SKU", "Documented"],
      ["MOQ negotiation", "From 50 units"],
    ],
  },
  {
    num: "03",
    title: "Quality control",
    body: "Every order is documented. In-line at production, AQL pre-shipment with photo evidence, and a final container loading check. No surprises on landing.",
    deliverables: [
      ["AQL inspection", "Standard 2.5"],
      ["In-line QC visits", "Per milestone"],
      ["Photo-evidence reports", "Within 48 hours"],
      ["Reject management", "Documented, costed"],
    ],
  },
  {
    num: "04",
    title: "Logistics & export",
    body: "Consolidation in our Delhi base, container loading supervised, export documentation handled, six ports active. You get one bill of lading and one ship date.",
    deliverables: [
      ["Consolidation hub", "Delhi NCR"],
      ["Container loading supervision", "Photographed"],
      ["Export documentation", "CHA-handled"],
      ["Active ports", "6 worldwide"],
    ],
  },
];

export const Capabilities = () => {
  useSeo({
    title: "Services · PD · Sourcing · QC · Logistics",
    description:
      "Product development, vetted factory sourcing (8 Indian clusters), AQL quality control with photo evidence, and export logistics from 6 active ports.",
    path: "/capabilities",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        SERVICES_LD,
        breadcrumb([
          { name: "Home", path: "/" },
          { name: "Services", path: "/capabilities" },
        ]),
      ],
    },
    jsonLdId: "capabilities-jsonld",
  });

  return (
    <div data-testid="capabilities-page">
      <header className="page-head wrap">
        <Reveal as="p" className="eyebrow">Capabilities · 04</Reveal>
        <WordReveal as="h1" text="What we run, end to end." stagger={70} />
        <Reveal as="p" className="lede" delay={200}>
          The work breaks into four disciplines. We can take the whole order — from a sketched brief
          to a container on the water — or step in at any single point.
        </Reveal>
      </header>

      {CAPABILITIES.map((cap) => (
        <section className="cap-row wrap" key={cap.num} data-testid={`capability-${cap.num}`}>
          <div className="cap-grid">
            <Reveal>
              <span className="cap-num">/ {cap.num}</span>
              <h2>{cap.title}</h2>
              <p>{cap.body}</p>
            </Reveal>
            <Reveal>
              <ul className="cap-list" aria-label={`${cap.title} deliverables`}>
                {cap.deliverables.map(([k, v]) => (
                  <li key={k}>
                    <span>{k}</span>
                    <span>{v}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </section>
      ))}

      <section className="cta wrap">
        <Reveal as="p" className="eyebrow">Start an enquiry</Reveal>
        <Reveal as="h2" className="display">Pick a discipline. Or all four.</Reveal>
        <Reveal className="row">
          <Link to="/contact" className="btn btn-fill btn-lg" data-testid="capabilities-cta">
            Send an enquiry
          </Link>
          <Link to="/clusters" className="btn btn-ghost">See the clusters</Link>
        </Reveal>
      </section>
    </div>
  );
};

export default Capabilities;
